import React, { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from './firebaseConfig';
import { useAuth } from './AuthContext';

export default function Login() {
  const { signInWithGoogle, login, signup } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const handleGoogleSignIn = async () => {
    setError(''); setLoading(true);
    try { await signInWithGoogle(); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (mode === 'signup') {
        await signup(email, password, email.split('@')[0]);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Check your inbox — a reset link is on its way.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  return (
    <>
      <div style={styles.bg} />

      <div style={{ ...styles.container, opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(20px)' }}>

        {/* Logo */}
        <div style={styles.logoSection}>
          <div style={styles.logoIcon}>🎲</div>
          <h1 style={styles.logoText}>Mokshapatra</h1>
          <p style={styles.tagline}>Your journey to liberation begins with a single roll</p>
        </div>

        {/* Card */}
        <div style={styles.card}>

          {/* ── Forgot password mode ── */}
          {mode === 'forgot' ? (
            <>
              <h2 style={styles.cardTitle}>Reset Password</h2>
              <p style={styles.cardSubtitle}>Enter your email and we'll send a reset link</p>

              {success ? (
                <div style={styles.successBox}>{success}</div>
              ) : (
                <form onSubmit={handleForgotPassword} style={styles.form}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      required style={styles.input} placeholder="your@email.com" disabled={loading} />
                  </div>
                  {error && <div style={styles.errorBox}><span>⚠️</span> {error}</div>}
                  <button type="submit" disabled={loading} style={{ ...styles.primaryBtn, opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}

              <button onClick={() => switchMode('signin')} style={styles.linkBtn}>
                ← Back to sign in
              </button>
            </>
          ) : (
            <>
              {/* ── Sign in / Sign up mode ── */}
              <h2 style={styles.cardTitle}>{mode === 'signup' ? 'Begin Your Journey' : 'Welcome Back'}</h2>
              <p style={styles.cardSubtitle}>
                {mode === 'signup' ? 'Create your account to start your spiritual practice' : 'Continue your path to inner peace'}
              </p>

              {/* Google */}
              <button onClick={handleGoogleSignIn} disabled={loading} style={{ ...styles.googleBtn, opacity: loading ? 0.7 : 1 }}>
                <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div style={styles.divider}>
                <div style={styles.dividerLine} />
                <span style={styles.dividerText}>or</span>
                <div style={styles.dividerLine} />
              </div>

              <form onSubmit={handleEmailAuth} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required style={styles.input} placeholder="your@email.com" disabled={loading} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    required style={styles.input} placeholder="••••••••" disabled={loading} minLength={6} />
                </div>

                {error && <div style={styles.errorBox}><span>⚠️</span> {error}</div>}

                <button type="submit" disabled={loading}
                  style={{ ...styles.primaryBtn, opacity: loading ? 0.7 : 1 }}>
                  {loading ? '...' : mode === 'signup' ? 'Create Account' : 'Sign In'}
                </button>
              </form>

              {/* Forgot password — only on sign-in */}
              {mode === 'signin' && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button onClick={() => switchMode('forgot')} style={styles.linkBtn}>
                    Forgot password?
                  </button>
                </div>
              )}

              <div style={styles.toggleRow}>
                <span style={styles.toggleText}>
                  {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
                </span>
                <button onClick={() => switchMode(mode === 'signup' ? 'signin' : 'signup')}
                  style={styles.toggleBtn} disabled={loading}>
                  {mode === 'signup' ? 'Sign In' : 'Sign Up'}
                </button>
              </div>
            </>
          )}
        </div>

        <p style={styles.quote}>"The journey of a thousand miles begins with a single step"</p>
      </div>

      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
      `}</style>
    </>
  );
}

const styles = {
  bg: {
    position: 'fixed', inset: 0, background: '#0D0D2B', zIndex: -1,
  },
  container: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 20px', transition: 'all 0.6s ease',
  },
  logoSection: { textAlign: 'center', marginBottom: '36px' },
  logoIcon: { fontSize: '56px', marginBottom: '12px', display: 'block', animation: 'float 3s ease-in-out infinite' },
  logoText: {
    fontFamily: '"Cinzel", serif', fontSize: '36px', fontWeight: '700',
    color: '#C9920A', margin: '0 0 10px 0', letterSpacing: '2px',
  },
  tagline: { fontSize: '15px', color: 'rgba(245,240,232,0.6)', margin: 0, lineHeight: '1.6' },

  card: {
    background: '#1E1145', border: '1px solid rgba(201,146,10,0.25)',
    borderRadius: '20px', padding: '40px 32px',
    maxWidth: '420px', width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  cardTitle: {
    fontFamily: '"Cinzel", serif', fontSize: '22px', fontWeight: '700',
    color: '#F5F0E8', marginBottom: '8px', textAlign: 'center', marginTop: 0,
  },
  cardSubtitle: {
    fontSize: '14px', color: 'rgba(245,240,232,0.55)',
    marginBottom: '28px', textAlign: 'center', lineHeight: '1.5',
  },

  googleBtn: {
    width: '100%', padding: '14px', background: '#F5F0E8',
    border: 'none', borderRadius: '12px', fontSize: '15px',
    fontWeight: '600', color: '#1A0533', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    fontFamily: '"Lato", sans-serif', transition: 'all 0.2s',
  },
  divider: { display: 'flex', alignItems: 'center', margin: '20px 0' },
  dividerLine: { flex: 1, height: '1px', background: 'rgba(245,240,232,0.12)' },
  dividerText: { padding: '0 14px', fontSize: '13px', color: 'rgba(245,240,232,0.35)' },

  form: { width: '100%' },
  inputGroup: { marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'rgba(245,240,232,0.7)', marginBottom: '8px' },
  input: {
    width: '100%', padding: '13px 14px', fontSize: '15px',
    border: '1px solid rgba(201,146,10,0.25)', borderRadius: '10px',
    background: 'rgba(255,255,255,0.05)', color: '#F5F0E8',
    fontFamily: '"Lato", sans-serif', boxSizing: 'border-box', outline: 'none',
  },

  errorBox: {
    padding: '12px 14px', background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px',
    color: '#fca5a5', fontSize: '13px', marginBottom: '16px',
    display: 'flex', gap: '8px', alignItems: 'flex-start',
  },
  successBox: {
    padding: '14px', background: 'rgba(34,197,94,0.12)',
    border: '1px solid rgba(34,197,94,0.3)', borderRadius: '10px',
    color: '#86efac', fontSize: '14px', marginBottom: '20px', lineHeight: '1.5',
  },

  primaryBtn: {
    width: '100%', padding: '15px', background: '#C9920A',
    border: 'none', borderRadius: '12px', fontSize: '16px',
    fontWeight: '700', color: '#0D0D2B', cursor: 'pointer',
    fontFamily: '"Lato", sans-serif', marginTop: '4px',
  },

  linkBtn: {
    background: 'none', border: 'none', color: 'rgba(201,146,10,0.8)',
    fontSize: '13px', cursor: 'pointer', textDecoration: 'underline', padding: '4px 0',
  },

  toggleRow: {
    marginTop: '24px', textAlign: 'center',
    paddingTop: '20px', borderTop: '1px solid rgba(245,240,232,0.08)',
  },
  toggleText: { fontSize: '13px', color: 'rgba(245,240,232,0.45)', marginRight: '6px' },
  toggleBtn: {
    background: 'none', border: 'none', color: '#C9920A',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },

  quote: {
    marginTop: '28px', fontSize: '13px', color: 'rgba(245,240,232,0.35)',
    fontStyle: 'italic', textAlign: 'center', animation: 'fadeIn 1s ease-out 0.8s backwards',
  },
};
